import { getPublicRuntimeConfig } from "@/lib/config/publicRuntimeConfig";

export type BrandSeoProfileId = "ikai" | "gaiai" | "customer";

export interface BrandSeoLink {
  href: string;
  label: string;
  description?: string;
  badge?: string;
  external?: boolean;
  rel?: string;
}

export interface BrandSeoNavLink extends BrandSeoLink {
  kind?: "link" | "dropdown" | "mega-menu";
  children?: BrandSeoLink[];
}

export interface BrandSeoFooterGroup {
  title: string;
  links: BrandSeoLink[];
}

export interface BrandSeoContactProfile {
  email: string;
  contactHref: string;
  phoneDisplay?: string;
  phoneE164?: string;
  whatsappHref?: string;
}

export interface BrandSeoCtaProfile {
  href: string;
  label: string;
  title: string;
  description: string;
  channel: "signup" | "contact" | "whatsapp";
}

export interface BrandSeoPublicShellPolicy {
  mode: "product" | "brand";
  hideAuthLinks: boolean;
  showLogin: boolean;
  showRegister: boolean;
  showGoogleLogin: boolean;
  loginHref: string;
  registerHref: string;
  accountHref: string;
}

export interface BrandSeoBlogImagePolicy {
  defaultOgImagePath: string;
  defaultHeroImagePath: string;
  firstPostImageAlt: string;
  mediaPathPrefix: string;
  allowedExtensions: string[];
  fileNamePattern: string;
}

export interface BrandSeoBlogCategoryProfile {
  slug: string;
  label: string;
  description: string;
  colorClass: string;
}

export interface BrandSeoProfile {
  id: BrandSeoProfileId;
  siteUrl: string;
  organizationName: string;
  organizationAlternateName: string;
  productName: string;
  titleSuffix: string;
  blogName: string;
  blogDescription: string;
  defaultAuthorName: string;
  creator: string;
  publisher: string;
  contactEmail: string;
  logoPath: string;
  publicLogoPath: string;
  logoAlt: string;
  brandMarkText: string;
  defaultOgImagePath: string;
  socialHandle?: string;
  sameAs: string[];
  socialLinks: BrandSeoLink[];
  navLinks: BrandSeoNavLink[];
  footerGroups: BrandSeoFooterGroup[];
  footerPopularLinks: BrandSeoLink[];
  contact: BrandSeoContactProfile;
  primaryCta: BrandSeoCtaProfile;
  secondaryCta?: BrandSeoCtaProfile;
  publicShell: BrandSeoPublicShellPolicy;
  blogImagePolicy: BrandSeoBlogImagePolicy;
  blogCategories: BrandSeoBlogCategoryProfile[];
  blogIndexBoostLinks: BrandSeoLink[];
  schemaOrganizationDescription: string;
  schemaSlogan: string;
  schemaApplicationSubCategory: string;
  schemaFeatureList: string[];
  schemaVideoPath: string;
  schemaVideoDescription: string;
  ctaHref: string;
  ctaLabel: string;
  ctaTitle: string;
  ctaDescription: string;
}

const IKAI_HOSTS = new Set([
  "ikai.com.tr",
  "www.ikai.com.tr",
  "dev.ikai.com.tr",
  "localhost",
  "127.0.0.1",
]);

const GAIAI_HOSTS = new Set([
  "gaiai.ai",
  "www.gaiai.ai",
  "blog.gaiai.ai",
  "ik.gaiai.ai",
]);
const GAIAI_CUSTOMER_MODE_HOSTS = new Set(["blog.gaiai.ai"]);
const IKAI_CANONICAL_SITE_URL = "https://ikai.com.tr";
const GAIAI_CANONICAL_SITE_URL = "https://gaiai.ai";
const BLOG_MEDIA_FILE_NAME_PATTERN =
  "^[a-zA-Z0-9-]+-(?:original|og|thumb)\\.webp$";
const IKAI_WHATSAPP_URL =
  "https://wa.me/19129224004?text=Merhaba,%20IKAI%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum.%20Demo,%20fiyatland%C4%B1rma%20ve%20%C5%9Firketimize%20uygun%20kullan%C4%B1m%20senaryosu%20i%C3%A7in%20yard%C4%B1mc%C4%B1%20olur%20musunuz%3F";
const GAIAI_WHATSAPP_URL =
  "https://wa.me/905398827540?text=Merhaba%2C%20GAIAI%20Agents%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum.";

function envValue(key: string): string {
  if (typeof process === "undefined") return "";
  return String(process.env[key] || "");
}

function envList(key: string): string[] {
  return envValue(key)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function envFirst(keys: string[], fallback: string): string {
  for (const key of keys) {
    const value = envValue(key);
    if (value) return value;
  }
  return fallback;
}

function isExplicitCustomerMode(): boolean {
  return ["NEXT_PUBLIC_BRAND_PROFILE_ID", "NEXT_PUBLIC_BRAND_MODE"].some(
    (key) => envValue(key).toLowerCase() === "customer"
  );
}

const IKAI_PROFILE_BASE: Omit<BrandSeoProfile, "siteUrl"> = {
  id: "ikai",
  organizationName: "IKAI HR",
  organizationAlternateName: "IKAI HR Platform - ATS + HRIS + AI",
  productName: "IKAI HR Platform",
  titleSuffix: "IKAI HR",
  blogName: "IKAI Blog",
  blogDescription:
    "İnsan kaynakları dünyasından trendler, AI destekli işe alım, onboarding, performans yönetimi, analitik ve başarı hikayeleri.",
  defaultAuthorName: "IKAI İçerik Ekibi",
  creator: "GAIAI",
  publisher: "GAIAI",
  contactEmail: "info@ikai.com.tr",
  logoPath: "/logo.png",
  publicLogoPath: "/ikai-logo.webp",
  logoAlt: "IKAI HR",
  brandMarkText: "İ",
  defaultOgImagePath: "/og-default.jpg",
  socialHandle: "@ikaiHR",
  sameAs: [
    "https://www.linkedin.com/company/ikai-hr-platform-ats-hris-ai/",
    "https://www.instagram.com/ikai.hr/",
  ],
  socialLinks: [
    {
      href: "https://www.linkedin.com/company/ikai-hr-platform-ats-hris-ai/",
      label: "IKAI HR LinkedIn",
      external: true,
      rel: "noopener noreferrer",
    },
    {
      href: "https://www.instagram.com/ikai.hr/",
      label: "IKAI HR Instagram",
      external: true,
      rel: "noopener noreferrer",
    },
  ],
  navLinks: [
    { href: "/", label: "Ana Sayfa" },
    { href: "/features", label: "Özellikler" },
    { href: "/cozumler", label: "Çözümler", kind: "mega-menu" },
    {
      href: "/blog",
      label: "Kaynaklar",
      kind: "dropdown",
      children: [
        {
          href: "/blog",
          label: "Blog",
          description: "İK trendleri ve ipuçları",
        },
        {
          href: "/yazilim",
          label: "İK Yazılımları",
          description: "Yazılım rehberleri",
        },
        {
          href: "/araclar",
          label: "Ücretsiz Araçlar",
          description: "İzin hesaplayıcı vb.",
        },
      ],
    },
    { href: "/pricing", label: "Fiyatlandırma" },
  ],
  footerGroups: [
    {
      title: "Ürün",
      links: [
        { href: "/features", label: "Özellikler" },
        { href: "/pulse", label: "Pulse AI", badge: "YENİ" },
        { href: "/pricing", label: "Fiyatlandırma" },
        { href: "/register", label: "Ücretsiz Başla" },
        {
          href: "https://ikai.com.tr/presentation.html",
          label: "Ürün Sunumu",
          external: true,
          rel: "noopener noreferrer nofollow",
        },
        { href: "/", label: "API Referansı" },
      ],
    },
    {
      title: "Kaynaklar",
      links: [
        { href: "/blog", label: "Blog" },
        { href: "/cozumler", label: "Çözümler" },
        { href: "/yazilim", label: "İK Yazılımları" },
        { href: "/sektorler", label: "Sektörler" },
        { href: "/araclar", label: "Ücretsiz Araçlar" },
      ],
    },
    {
      title: "Şirket",
      links: [
        { href: "/about", label: "Hakkımızda" },
        { href: "/iletisim", label: "İletişim" },
        { href: "/privacy", label: "Gizlilik Politikası" },
        { href: "/terms", label: "Kullanım Şartları" },
      ],
    },
  ],
  footerPopularLinks: [
    { href: "/yazilim/ik-yazilimi", label: "İK Yazılımı" },
    { href: "/yazilim/hr-yazilimi", label: "HR Yazılımı" },
    { href: "/yazilim/personel-yonetimi-yazilimi", label: "Personel Yönetimi" },
    { href: "/yazilim/aday-takip-sistemi", label: "Aday Takip Sistemi" },
    { href: "/yazilim/ats-yazilimi", label: "ATS Yazılımı" },
    { href: "/yazilim/cv-analiz-programi", label: "CV Analiz Programı" },
    { href: "/yazilim/ai-cv-analizi", label: "AI CV Analizi" },
    { href: "/yazilim/bordro-yazilimi", label: "Bordro Yazılımı" },
    { href: "/yazilim/izin-takip-programi", label: "İzin Takip Programı" },
    { href: "/yazilim/mesai-takip-yazilimi", label: "Mesai Takip Sistemi" },
    {
      href: "/yazilim/performans-degerlendirme-yazilimi",
      label: "Performans Yazılımı",
    },
    { href: "/yazilim/hris-yazilimi", label: "HRIS Sistemi" },
    { href: "/araclar/izin-hesaplayici", label: "İzin Hesaplayıcı" },
    { href: "/araclar/maas-hesaplayici", label: "Maaş Hesaplayıcı" },
  ],
  contact: {
    email: "info@ikai.com.tr",
    contactHref: "/iletisim",
    phoneDisplay: "+1 912 922 4004",
    phoneE164: "+19129224004",
    whatsappHref: IKAI_WHATSAPP_URL,
  },
  primaryCta: {
    href: "/register",
    label: "Ücretsiz Başlayın",
    title: "İK Süreçlerinizi Dijitalleştirin",
    description:
      "Blogda okuduklarınızı IKAI ile pratiğe dökün. 14 gün ücretsiz deneyin.",
    channel: "signup",
  },
  secondaryCta: {
    href: IKAI_WHATSAPP_URL,
    label: "WhatsApp'tan Yazın",
    title: "IKAI hakkında bilgi alın",
    description:
      "Demo, fiyatlandırma ve kullanım senaryosu için iletişime geçin.",
    channel: "whatsapp",
  },
  publicShell: {
    mode: "product",
    hideAuthLinks: false,
    showLogin: true,
    showRegister: true,
    showGoogleLogin: true,
    loginHref: "/login",
    registerHref: "/register",
    accountHref: "/dashboard",
  },
  blogImagePolicy: {
    defaultOgImagePath: "/og-default.jpg",
    defaultHeroImagePath: "/og-default.jpg",
    firstPostImageAlt: "IKAI HR blog görseli",
    mediaPathPrefix: "/api/v1/public/blog/media",
    allowedExtensions: ["webp"],
    fileNamePattern: BLOG_MEDIA_FILE_NAME_PATTERN,
  },
  schemaOrganizationDescription:
    "Türkiye'nin AI destekli İK yazılımı. İşe alımdan çıkışa kadar tüm insan kaynakları süreçlerinizi tek platformda yönetin.",
  schemaSlogan:
    "İşe alımdan performansa, yapay zeka destekli uçtan uca İK yönetim platformu.",
  schemaApplicationSubCategory: "Human Resources Software",
  schemaFeatureList: [
    "AI CV Analizi",
    "İşe Alım Yönetimi (ATS)",
    "Çalışan Yönetimi (HRIS)",
    "İzin Takibi",
    "Mesai Takibi",
    "Performans Değerlendirme",
    "Bordro Yönetimi",
    "Raporlama ve Analitik",
    "E-imza Entegrasyonu",
  ],
  schemaVideoPath: "/videos/ikai-platform-tanitimi.mp4",
  schemaVideoDescription:
    "IKAI HR Platform ile İK süreçlerinizi yapay zeka ile otomatikleştirin.",
  blogCategories: [
    {
      slug: "ik-trendleri",
      label: "İK Trendleri",
      description:
        "İnsan kaynakları trendleri ve yapay zeka destekli iş akışları.",
      colorClass: "bg-blue-100 text-blue-700",
    },
    {
      slug: "rehberler",
      label: "Rehberler",
      description: "İK operasyonları için uygulanabilir rehberler.",
      colorClass: "bg-green-100 text-green-700",
    },
    {
      slug: "guncellemeler",
      label: "Güncellemeler",
      description: "IKAI ürün ve platform güncellemeleri.",
      colorClass: "bg-orange-100 text-orange-700",
    },
    {
      slug: "basari-hikayeleri",
      label: "Başarı Hikayeleri",
      description: "IKAI ile iyileştirilen iş akışlarından notlar.",
      colorClass: "bg-teal-100 text-teal-700",
    },
    {
      slug: "ikai-ozellikleri",
      label: "IKAI Özellikleri",
      description: "IKAI ürün kabiliyetleri ve kullanım senaryoları.",
      colorClass: "bg-slate-100 text-slate-700",
    },
  ],
  blogIndexBoostLinks: [],
  ctaHref: "/register",
  ctaLabel: "Ücretsiz Başlayın",
  ctaTitle: "İK Süreçlerinizi Dijitalleştirin",
  ctaDescription:
    "Blogda okuduklarınızı IKAI ile pratiğe dökün. 14 gün ücretsiz deneyin.",
};

const GAIAI_PROFILE_BASE: Omit<BrandSeoProfile, "siteUrl"> = {
  id: "gaiai",
  organizationName: "GAIAI",
  organizationAlternateName: "GAIAI Agent-Native Growth Office",
  productName: "GAIAI",
  titleSuffix: "GAIAI",
  blogName: "GAIAI Blog",
  blogDescription:
    "GAIAI Agents, müşteri görüşmelerinde ve izinli Google Chat ekip işlerinde çalışabilen ajanlar sunar.",
  defaultAuthorName: "GAIAI İçerik Ekibi",
  creator: "GAIAI",
  publisher: "GAIAI",
  contactEmail: "info@gaiai.ai",
  logoPath: "/gaiai-agents/web/pwa-icon-512.png",
  publicLogoPath: "/gaiai-agents/web/pwa-icon-512.png",
  logoAlt: "GAIAI",
  brandMarkText: "G",
  defaultOgImagePath: "/gaiai-agents/social/gaiai-agents-og-tr-1200x630.png",
  sameAs: [],
  socialLinks: [
    {
      href: "https://gaiai.ai",
      label: "GAIAI",
      external: true,
      rel: "noopener noreferrer",
    },
  ],
  navLinks: [
    { href: "/chat-bridge/landing", label: "Ana Sayfa" },
    { href: "/chat-bridge/features", label: "Ajanlar" },
    { href: "/chat-bridge/pricing", label: "Fiyatlar" },
    { href: "/chat-bridge/about", label: "Hakkımızda" },
    { href: "/blog", label: "Blog" },
    { href: "/chat-bridge/contact", label: "İletişim" },
  ],
  footerGroups: [
    {
      title: "GAIAI",
      links: [
        { href: "/chat-bridge/landing", label: "Ana Sayfa" },
        { href: "/chat-bridge/features", label: "Ajanlar" },
        { href: "/chat-bridge/pricing", label: "Fiyatlar" },
        { href: "/chat-bridge/about", label: "Hakkımızda" },
        { href: "/blog", label: "Blog" },
        { href: "/chat-bridge/contact", label: "İletişim" },
        { href: "mailto:info@gaiai.ai", label: "E-posta" },
      ],
    },
  ],
  footerPopularLinks: [],
  contact: {
    email: "info@gaiai.ai",
    contactHref: "mailto:info@gaiai.ai",
    phoneDisplay: "+90 539 882 7540",
    phoneE164: "+905398827540",
    whatsappHref: GAIAI_WHATSAPP_URL,
  },
  primaryCta: {
    href: GAIAI_WHATSAPP_URL,
    label: "WhatsApp ile görüşün",
    title: "GAIAI Agents hakkında görüşelim",
    description:
      "Müşteri görüşmeleri ve ekip iş akışları için GAIAI ekibine ulaşın.",
    channel: "whatsapp",
  },
  secondaryCta: {
    href: "mailto:info@gaiai.ai",
    label: "E-posta Gönder",
    title: "GAIAI ile e-posta üzerinden görüşün",
    description:
      "GAIAI Agents hakkında sorularınızı info@gaiai.ai adresine yazın.",
    channel: "contact",
  },
  publicShell: {
    mode: "brand",
    hideAuthLinks: true,
    showLogin: false,
    showRegister: false,
    showGoogleLogin: false,
    loginHref: "/chat-bridge/contact",
    registerHref: "/chat-bridge/contact",
    accountHref: "/chat-bridge/contact",
  },
  blogImagePolicy: {
    defaultOgImagePath: "/gaiai-agents/social/gaiai-agents-og-tr-1200x630.png",
    defaultHeroImagePath:
      "/gaiai-agents/social/gaiai-agents-og-tr-1200x630.png",
    firstPostImageAlt: "GAIAI AI ajan iş sistemleri",
    mediaPathPrefix: "/api/v1/public/blog/media",
    allowedExtensions: ["webp"],
    fileNamePattern: BLOG_MEDIA_FILE_NAME_PATTERN,
  },
  schemaOrganizationDescription:
    "GAIAI Agents ile müşteri görüşmeleri ve ekip iş akışları.",
  schemaSlogan: "AI-Native Operasyonunuzu Netleştirin",
  schemaApplicationSubCategory: "AI-Native Operations Platform",
  schemaFeatureList: [
    "SEO ve içerik üretim akışı",
    "Kanıt odaklı karar kayıtları",
    "AI ajan iş sistemleri",
    "Büyüme ve operasyon danışmanlığı",
    "Kurumsal ölçekte ölçeklenebilir otomasyon",
  ],
  schemaVideoPath: "/videos/gaiai-platform-tanitimi.mp4",
  schemaVideoDescription:
    "GAIAI ile AI-native operasyon yaklaşımınızı güvenli ve ölçülebilir biçimde ölçeklendirin.",
  blogCategories: [
    {
      slug: "ai-ajan-is-sistemleri",
      label: "AI Ajan İş Sistemleri",
      description:
        "AI-native iş sistemleri, agent destekli operasyon ve büyüme iş akışları.",
      colorClass: "bg-teal-100 text-teal-700",
    },
    {
      slug: "ik-trendleri",
      label: "AI-Native Operasyon Trendleri",
      description:
        "AI-native büyüme, SEO, operasyon ve agent destekli iş akışları için uygulanabilir trendler.",
      colorClass: "bg-blue-100 text-blue-700",
    },
    {
      slug: "rehberler",
      label: "Rehberler",
      description:
        "AI-native operasyon ve büyüme iş akışları için adım adım rehberler.",
      colorClass: "bg-green-100 text-green-700",
    },
    {
      slug: "guncellemeler",
      label: "GAIAI Güncellemeleri",
      description: "GAIAI ürün, operasyon ve büyüme sistemi güncellemeleri.",
      colorClass: "bg-orange-100 text-orange-700",
    },
    {
      slug: "basari-hikayeleri",
      label: "Operasyon Başarı Hikayeleri",
      description:
        "AI-native operasyon yaklaşımıyla iyileştirilen süreçlerden uygulanabilir notlar.",
      colorClass: "bg-teal-100 text-teal-700",
    },
  ],
  blogIndexBoostLinks: [],
  ctaHref: GAIAI_WHATSAPP_URL,
  ctaLabel: "WhatsApp ile görüşün",
  ctaTitle: "GAIAI Agents hakkında görüşelim",
  ctaDescription:
    "Müşteri görüşmeleri ve ekip iş akışları için GAIAI ekibine ulaşın.",
};

function customerNavLinks(): BrandSeoNavLink[] {
  return [
    { href: "/", label: "Ana Sayfa" },
    { href: "/blog", label: "Blog" },
    {
      href: envFirst(["NEXT_PUBLIC_BRAND_CONTACT_HREF"], "/iletisim"),
      label: "İletişim",
    },
  ];
}

function customerFooterGroups(organizationName: string): BrandSeoFooterGroup[] {
  return [
    {
      title: organizationName,
      links: [
        { href: "/", label: "Ana Sayfa" },
        { href: "/blog", label: "Blog" },
        {
          href: envFirst(["NEXT_PUBLIC_BRAND_CONTACT_HREF"], "/iletisim"),
          label: "İletişim",
        },
      ],
    },
  ];
}

function customerContact(): BrandSeoContactProfile {
  return {
    email: envFirst(
      ["NEXT_PUBLIC_BRAND_CONTACT_EMAIL"],
      GAIAI_PROFILE_BASE.contactEmail
    ),
    contactHref: envFirst(["NEXT_PUBLIC_BRAND_CONTACT_HREF"], "/iletisim"),
    phoneDisplay: envValue("NEXT_PUBLIC_BRAND_PHONE_DISPLAY") || undefined,
    phoneE164: envValue("NEXT_PUBLIC_BRAND_PHONE_E164") || undefined,
    whatsappHref: envValue("NEXT_PUBLIC_BRAND_WHATSAPP_HREF") || undefined,
  };
}

function customerPrimaryCta(): BrandSeoCtaProfile {
  return {
    href: envFirst(["NEXT_PUBLIC_BRAND_CTA_HREF"], "/iletisim"),
    label: envFirst(["NEXT_PUBLIC_BRAND_CTA_LABEL"], "İletişime Geçin"),
    title: envFirst(
      ["NEXT_PUBLIC_BRAND_CTA_TITLE"],
      "Operasyonunuzu Netleştirin"
    ),
    description: envFirst(
      ["NEXT_PUBLIC_BRAND_CTA_DESCRIPTION"],
      "Blogdaki yaklaşımı kendi büyüme ve operasyon akışınıza uyarlamak için iletişime geçin."
    ),
    channel: "contact",
  };
}

function customerBlogImagePolicy(
  organizationName: string
): BrandSeoBlogImagePolicy {
  return {
    defaultOgImagePath: envFirst(
      ["NEXT_PUBLIC_BRAND_OG_IMAGE_PATH"],
      "/og-default.jpg"
    ),
    defaultHeroImagePath: envFirst(
      ["NEXT_PUBLIC_BRAND_HERO_IMAGE_PATH", "NEXT_PUBLIC_BRAND_OG_IMAGE_PATH"],
      "/og-default.jpg"
    ),
    firstPostImageAlt: envFirst(
      ["NEXT_PUBLIC_BRAND_FIRST_POST_IMAGE_ALT"],
      `${organizationName} blog görseli`
    ),
    mediaPathPrefix: "/api/v1/public/blog/media",
    allowedExtensions: ["webp"],
    fileNamePattern: BLOG_MEDIA_FILE_NAME_PATTERN,
  };
}

function customerPublicShell(): BrandSeoPublicShellPolicy {
  return {
    mode: "brand",
    hideAuthLinks: true,
    showLogin: false,
    showRegister: false,
    showGoogleLogin: false,
    loginHref: "/#contact",
    registerHref: "/#contact",
    accountHref: "/#contact",
  };
}

function customerProfileBase(): Omit<BrandSeoProfile, "siteUrl"> {
  const organizationName = envFirst(
    ["NEXT_PUBLIC_BRAND_ORGANIZATION_NAME", "NEXT_PUBLIC_BRAND_NAME"],
    GAIAI_PROFILE_BASE.organizationName
  );
  const blogName = envFirst(
    ["NEXT_PUBLIC_BRAND_BLOG_NAME"],
    `${organizationName} Blog`
  );

  return {
    id: "customer",
    organizationName,
    organizationAlternateName: envFirst(
      ["NEXT_PUBLIC_BRAND_ALTERNATE_NAME"],
      organizationName
    ),
    productName: envFirst(["NEXT_PUBLIC_BRAND_PRODUCT_NAME"], organizationName),
    titleSuffix: envFirst(["NEXT_PUBLIC_BRAND_TITLE_SUFFIX"], organizationName),
    blogName,
    blogDescription: envFirst(
      ["NEXT_PUBLIC_BRAND_BLOG_DESCRIPTION"],
      GAIAI_PROFILE_BASE.blogDescription
    ),
    defaultAuthorName: envFirst(
      ["NEXT_PUBLIC_BRAND_DEFAULT_AUTHOR"],
      `${organizationName} İçerik Ekibi`
    ),
    creator: envFirst(["NEXT_PUBLIC_BRAND_CREATOR"], organizationName),
    publisher: envFirst(["NEXT_PUBLIC_BRAND_PUBLISHER"], organizationName),
    contactEmail: envFirst(
      ["NEXT_PUBLIC_BRAND_CONTACT_EMAIL"],
      GAIAI_PROFILE_BASE.contactEmail
    ),
    logoPath: envFirst(["NEXT_PUBLIC_BRAND_LOGO_PATH"], "/logo.png"),
    publicLogoPath: envFirst(
      ["NEXT_PUBLIC_BRAND_PUBLIC_LOGO_PATH", "NEXT_PUBLIC_BRAND_LOGO_PATH"],
      "/logo.png"
    ),
    logoAlt: envFirst(["NEXT_PUBLIC_BRAND_LOGO_ALT"], organizationName),
    brandMarkText: envFirst(
      ["NEXT_PUBLIC_BRAND_MARK_TEXT"],
      organizationName.charAt(0).toUpperCase()
    ),
    defaultOgImagePath: envFirst(
      ["NEXT_PUBLIC_BRAND_OG_IMAGE_PATH"],
      "/og-default.jpg"
    ),
    socialHandle: envValue("NEXT_PUBLIC_BRAND_SOCIAL_HANDLE") || undefined,
    sameAs: envList("NEXT_PUBLIC_BRAND_SAME_AS"),
    socialLinks: envList("NEXT_PUBLIC_BRAND_SAME_AS").map((href) => ({
      href,
      label: organizationName,
      external: true,
      rel: "noopener noreferrer",
    })),
    navLinks: customerNavLinks(),
    footerGroups: customerFooterGroups(organizationName),
    footerPopularLinks: [],
    contact: customerContact(),
    primaryCta: customerPrimaryCta(),
    publicShell: customerPublicShell(),
    blogImagePolicy: customerBlogImagePolicy(organizationName),
    blogCategories: [],
    blogIndexBoostLinks: [],
    schemaOrganizationDescription: envFirst(
      ["NEXT_PUBLIC_BRAND_SCHEMA_ORGANIZATION_DESCRIPTION"],
      envFirst(
        ["NEXT_PUBLIC_BRAND_BLOG_DESCRIPTION"],
        `${organizationName} için AI destekli içerik yaklaşımı.`
      )
    ),
    schemaSlogan: envFirst(
      ["NEXT_PUBLIC_BRAND_SCHEMA_SLOGAN"],
      envFirst(
        ["NEXT_PUBLIC_BRAND_DEFAULT_AUTHOR"],
        "Operasyonunuzu netleştirin."
      )
    ),
    schemaApplicationSubCategory: envFirst(
      ["NEXT_PUBLIC_BRAND_SCHEMA_APPLICATION_SUBCATEGORY"],
      "Business Software"
    ),
    schemaFeatureList: envList("NEXT_PUBLIC_BRAND_SCHEMA_FEATURES").length
      ? envList("NEXT_PUBLIC_BRAND_SCHEMA_FEATURES")
      : [
          `${organizationName} AI destekli içerik`,
          "Uygulanabilir operasyon ipuçları",
          "Ölçülebilir büyüme yaklaşımı",
        ],
    schemaVideoPath: envFirst(
      ["NEXT_PUBLIC_BRAND_SCHEMA_VIDEO_PATH"],
      "/videos/gaiai-platform-tanitimi.mp4"
    ),
    schemaVideoDescription: envFirst(
      ["NEXT_PUBLIC_BRAND_SCHEMA_VIDEO_DESCRIPTION"],
      envFirst(
        ["NEXT_PUBLIC_BRAND_CTA_DESCRIPTION"],
        "Kurumsal operasyonlarınız için uygulanabilir rehberlik alın."
      )
    ),
    ctaHref: envFirst(["NEXT_PUBLIC_BRAND_CTA_HREF"], "/iletisim"),
    ctaLabel: envFirst(["NEXT_PUBLIC_BRAND_CTA_LABEL"], "İletişime Geçin"),
    ctaTitle: envFirst(
      ["NEXT_PUBLIC_BRAND_CTA_TITLE"],
      "Operasyonunuzu Netleştirin"
    ),
    ctaDescription: envFirst(
      ["NEXT_PUBLIC_BRAND_CTA_DESCRIPTION"],
      "Blogdaki yaklaşımı kendi büyüme ve operasyon akışınıza uyarlamak için iletişime geçin."
    ),
  };
}

function cleanHost(value: string | null | undefined): string {
  return String(value || "")
    .split(",")[0]
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/:\d+$/, "")
    .toLowerCase();
}

function cleanOrigin(value: string): string {
  return new URL(value).toString().replace(/\/$/, "");
}

function profileWithSiteUrl(
  base: Omit<BrandSeoProfile, "siteUrl">,
  siteUrl: string
): BrandSeoProfile {
  return { ...base, siteUrl: cleanOrigin(siteUrl) };
}

function originFromHost(host: string, protocol = "https"): string | null {
  const normalizedHost = cleanHost(host);
  if (!normalizedHost) return null;
  const scheme =
    normalizedHost === "localhost" || normalizedHost === "127.0.0.1"
      ? "http"
      : protocol.replace(/:$/, "") || "https";
  return `${scheme}://${normalizedHost}`;
}

function sameKnownHostFamily(
  configuredHost: string,
  knownHosts: Set<string>
): boolean {
  return Boolean(configuredHost && knownHosts.has(configuredHost));
}

function resolveIkaiProfile(
  host: string,
  configuredSiteUrl: string | null,
  hostOrigin: string
) {
  const isLocalHost =
    host === "localhost" || host === "127.0.0.1" || host === "dev.ikai.com.tr";
  const configuredHost = configuredSiteUrl
    ? cleanHost(new URL(configuredSiteUrl).host)
    : "";
  const siteUrl = isLocalHost
    ? hostOrigin
    : sameKnownHostFamily(configuredHost, IKAI_HOSTS)
      ? configuredSiteUrl || IKAI_CANONICAL_SITE_URL
      : IKAI_CANONICAL_SITE_URL;
  return profileWithSiteUrl(IKAI_PROFILE_BASE, siteUrl);
}

function resolveGaiaiProfile() {
  return profileWithSiteUrl(GAIAI_PROFILE_BASE, GAIAI_CANONICAL_SITE_URL);
}

function resolveConfiguredOnlyProfile(
  configuredSiteUrl: string,
  configuredHost: string
) {
  if (IKAI_HOSTS.has(configuredHost)) {
    return profileWithSiteUrl(
      IKAI_PROFILE_BASE,
      configuredSiteUrl || IKAI_CANONICAL_SITE_URL
    );
  }
  if (GAIAI_HOSTS.has(configuredHost)) {
    if (
      GAIAI_CUSTOMER_MODE_HOSTS.has(configuredHost) &&
      isExplicitCustomerMode()
    ) {
      return profileWithSiteUrl(
        customerProfileBase(),
        configuredSiteUrl || GAIAI_CANONICAL_SITE_URL
      );
    }
    return profileWithSiteUrl(GAIAI_PROFILE_BASE, GAIAI_CANONICAL_SITE_URL);
  }
  return profileWithSiteUrl(
    customerProfileBase(),
    configuredSiteUrl || GAIAI_CANONICAL_SITE_URL
  );
}

function shouldUseConfiguredCustomerProfile(
  host: string,
  configuredHost: string
) {
  return Boolean(
    host &&
    configuredHost &&
    host === configuredHost &&
    (GAIAI_CUSTOMER_MODE_HOSTS.has(host) ? isExplicitCustomerMode() : true)
  );
}

function resolveConfiguredCustomerProfile(
  configuredSiteUrl: string | null,
  hostOrigin: string
) {
  return profileWithSiteUrl(
    customerProfileBase(),
    configuredSiteUrl || hostOrigin
  );
}

export function resolveBrandSeoProfileForHost(input: {
  host?: string | null;
  protocol?: string | null;
  configuredSiteUrl?: string | null;
}): BrandSeoProfile | null {
  const host = cleanHost(input.host);
  const configuredSiteUrl = input.configuredSiteUrl
    ? cleanOrigin(input.configuredSiteUrl)
    : null;
  const configuredHost = configuredSiteUrl
    ? cleanHost(new URL(configuredSiteUrl).host)
    : "";
  const hostOrigin = host
    ? originFromHost(host, input.protocol || "https")
    : configuredSiteUrl;

  if (!hostOrigin) return null;
  if (IKAI_HOSTS.has(host)) {
    return resolveIkaiProfile(host, configuredSiteUrl, hostOrigin);
  }
  if (
    shouldUseConfiguredCustomerProfile(host, configuredHost) &&
    !GAIAI_HOSTS.has(host)
  ) {
    return resolveConfiguredCustomerProfile(configuredSiteUrl, hostOrigin);
  }
  if (GAIAI_HOSTS.has(host)) {
    if (
      GAIAI_CUSTOMER_MODE_HOSTS.has(host) &&
      shouldUseConfiguredCustomerProfile(host, configuredHost)
    ) {
      return resolveConfiguredCustomerProfile(configuredSiteUrl, hostOrigin);
    }
    return resolveGaiaiProfile();
  }
  const configuredOnlySiteUrl = configuredSiteUrl;
  if (!host && configuredHost && configuredOnlySiteUrl) {
    return resolveConfiguredOnlyProfile(configuredOnlySiteUrl, configuredHost);
  }
  return null;
}

export function getBrandSeoProfile(): BrandSeoProfile {
  const runtimeConfig = getPublicRuntimeConfig();
  const profile = resolveBrandSeoProfileForHost({
    configuredSiteUrl: runtimeConfig.siteUrl,
  });
  if (!profile) throw new Error("BRAND_SEO_PROFILE_UNKNOWN_HOST");
  return profile;
}

export function getBrandGoogleSiteVerification(
  profile: BrandSeoProfile,
  ikaiFallback = ""
): string {
  if (profile.id === "gaiai") {
    return envValue("GAIAI_GOOGLE_SITE_VERIFICATION").trim();
  }
  if (profile.id === "ikai") {
    return (
      ikaiFallback ||
      getPublicRuntimeConfig().integrations.googleSiteVerification
    ).trim();
  }
  return "";
}

export function absoluteBrandUrl(
  profile: BrandSeoProfile,
  pathOrUrl: string
): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl.replace(
      /^https:\/\/(?:www\.)?ikai\.com\.tr/i,
      profile.siteUrl
    );
  }
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${profile.siteUrl}${path}`;
}

export function withBrandTitle(
  profile: BrandSeoProfile,
  title: string
): string {
  const cleanTitle = String(title || "")
    .replace(/\s*\|\s*IKAI(?:\s*HR)?\s*$/i, "")
    .replace(/\s*\|\s*GAIAI\s*$/i, "")
    .trim();
  return `${cleanTitle || profile.titleSuffix} | ${profile.titleSuffix}`;
}
